import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, LogOut, LayoutDashboard, FileText, Upload, Gavel,
  Settings, Coins, User, IdCard, Menu, X,
} from "lucide-react";
import { BookOpen } from "lucide-react";

/**
 * Desktop nav item — visible only at ≥md breakpoint. Preserved unchanged from the
 * pre-hamburger UI so existing testids and layout do not regress.
 */
const NavItem = ({ to, icon: Icon, children, testid }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition
       ${isActive ? "bg-purple-500/15 text-slate-900 ring-1 ring-purple-400/30" : "text-slate-700 hover:text-slate-900 hover:bg-slate-50"}`
    }
  >
    <Icon size={16} />
    <span>{children}</span>
  </NavLink>
);

/**
 * Mobile drawer row. Uses the same NavLink so React Router marks the active
 * route with `isActive`. Meets the 44×44 minimum tap target (min-h-[44px]).
 */
const DrawerItem = ({ to, icon: Icon, children, testid, onNavigate }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    onClick={onNavigate}
    className={({ isActive }) =>
      `flex items-center gap-3 rounded-xl px-4 min-h-[44px] py-2 text-base font-medium transition
       ${isActive ? "bg-purple-500/15 text-slate-900 ring-1 ring-purple-400/30" : "text-slate-800 hover:bg-slate-100"}`
    }
    aria-current={({ isActive }) => (isActive ? "page" : undefined)}
  >
    <Icon size={18} />
    <span>{children}</span>
  </NavLink>
);

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [menuOpen, setMenuOpen] = useState(false);
  const closeBtnRef = useRef(null);

  // Close the drawer whenever the route changes. Prevents the drawer from
  // staying visible if a NavLink triggers a route change without our onClick.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Escape-to-close + body-scroll-lock while the drawer is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    // Focus the close button so keyboard users can dismiss quickly.
    const t = setTimeout(() => closeBtnRef.current?.focus?.(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const handleSignOut = () => { closeMenu(); logout(); nav("/"); };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 glass-strong border-b border-slate-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
          <Link to="/app" className="flex items-center gap-2 min-w-0" data-testid="nav-logo">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 grid place-items-center shrink-0">
              <ShieldCheck size={18} className="text-white" />
            </div>
            <span className="font-semibold tracking-tight text-slate-900 truncate">Informed <span className="text-purple-600">Accelerator</span></span>
          </Link>
          {/* Desktop navigation — unchanged. Hidden at <md widths. */}
          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/app" icon={LayoutDashboard} testid="nav-dashboard">Dashboard</NavItem>
            <NavItem to="/app/upload" icon={Upload} testid="nav-upload">Upload</NavItem>
            <NavItem to="/app/documents" icon={FileText} testid="nav-documents">Documents</NavItem>
            <NavItem to="/app/letters" icon={BookOpen} testid="nav-letters">Letters</NavItem>
            <NavItem to="/app/identity" icon={IdCard} testid="nav-identity">Identity</NavItem>
            <NavItem to="/app/cfpb" icon={Gavel} testid="nav-cfpb">CFPB</NavItem>
            <NavItem to="/app/credits" icon={Coins} testid="nav-credits">Credits</NavItem>
            <NavItem to="/app/settings" icon={User} testid="nav-settings">Settings</NavItem>
            {isAdmin && <NavItem to="/app/admin" icon={Settings} testid="nav-admin">Admin</NavItem>}
          </nav>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-slate-500 max-w-[140px] truncate" data-testid="nav-user-email">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); nav("/"); }}
              data-testid="nav-logout-button"
              className="hidden md:inline-flex text-slate-700 hover:text-slate-900"
            >
              <LogOut size={15} className="mr-1" />Sign out
            </Button>
            {/* Mobile hamburger — visible only <md. 44×44 min tap target. */}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu-drawer"
              className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl text-slate-800 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer + overlay — rendered outside the header so it can cover the full viewport. */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="mobile-menu-title">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            data-testid="mobile-menu-overlay"
            onClick={closeMenu}
            aria-hidden="true"
          />
          <aside
            id="mobile-menu-drawer"
            data-testid="mobile-menu-drawer"
            className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white border-l border-slate-200 shadow-xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 h-16 border-b border-slate-200">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 grid place-items-center shrink-0">
                  <ShieldCheck size={18} className="text-white" />
                </div>
                <span id="mobile-menu-title" className="font-semibold tracking-tight text-slate-900 truncate">Menu</span>
              </div>
              <button
                type="button"
                ref={closeBtnRef}
                onClick={closeMenu}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="inline-flex items-center justify-center h-11 w-11 rounded-xl text-slate-800 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
              >
                <X size={20} />
              </button>
            </div>
            <div className="text-xs text-slate-500 px-4 pt-3 truncate" data-testid="mobile-menu-user-email">{user?.email}</div>
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              <DrawerItem to="/app"           icon={LayoutDashboard} testid="mobile-menu-dashboard"       onNavigate={closeMenu}>Dashboard</DrawerItem>
              <DrawerItem to="/app/upload"    icon={Upload}          testid="mobile-menu-upload-report"    onNavigate={closeMenu}>Upload Report</DrawerItem>
              <DrawerItem to="/app/documents" icon={FileText}        testid="mobile-menu-documents"        onNavigate={closeMenu}>Documents</DrawerItem>
              <DrawerItem to="/app/letters"   icon={BookOpen}        testid="mobile-menu-letters"          onNavigate={closeMenu}>Letter Library</DrawerItem>
              <DrawerItem to="/app/identity"  icon={IdCard}          testid="mobile-menu-identity"         onNavigate={closeMenu}>Identity Documents</DrawerItem>
              <DrawerItem to="/app/cfpb"      icon={Gavel}           testid="mobile-menu-cfpb"             onNavigate={closeMenu}>CFPB</DrawerItem>
              <DrawerItem to="/app/credits"   icon={Coins}           testid="mobile-menu-credits"          onNavigate={closeMenu}>Credits</DrawerItem>
              <DrawerItem to="/app/settings"  icon={User}            testid="mobile-menu-settings"         onNavigate={closeMenu}>Settings</DrawerItem>
              {isAdmin && (
                <DrawerItem to="/app/admin"   icon={Settings}        testid="mobile-menu-admin"            onNavigate={closeMenu}>Admin</DrawerItem>
              )}
            </nav>
            <div className="p-3 border-t border-slate-200">
              <Button
                variant="outline"
                onClick={handleSignOut}
                data-testid="mobile-menu-signout"
                className="w-full min-h-[44px] rounded-xl border-slate-200 bg-white text-slate-800 hover:bg-slate-100"
              >
                <LogOut size={16} className="mr-2" />Sign out
              </Button>
            </div>
          </aside>
        </div>
      )}

      <main className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
      </main>
    </div>
  );
}
